import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const galleryImages = [
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/10bc3c3b2c8248a851e356498c5ccbf9.jpeg", alt: "園での遊びの様子" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/90564d8968ffbd8aa4478926174e4a6a.jpeg", alt: "温かい保育風景" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/863eb0d46979dc716e148c0d3a0f6809.jpeg", alt: "保護者サポート" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/49539994332b288ab8c03b8d368c9e68.jpeg", alt: "健康的な食事" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/97125725b8284e3fbdde36e1d15ef634.jpeg", alt: "楽しい活動" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/f9d7f7033febbd63a7d490b9838ecc05.jpeg", alt: "トイレトレーニング設備" },
    { src: "https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/e7g2g0g0g0g0g0g0g0g0g0g0g0g0g0g0.jpeg", alt: "園の入口" },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/8964f2db9296a8798ab5bb6895e36bf1.jpeg" alt="きらり保育多賀城" className="h-12 w-auto" />
          </div>
          <div className="flex items-center">
            <a href="https://kirari-hoiku.com/tagajo/" target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all hover:scale-105 text-sm font-medium cursor-pointer whitespace-nowrap">園見学申込</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 relative min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/10bc3c3b2c8248a851e356498c5ccbf9.jpeg" 
            alt="温かい保育風景" 
            className="w-full h-full object-cover object-[75%_center] md:object-right" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/30"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-xl space-y-8">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              「人生の主役<br />（おほしさま）」<br />をつくる。
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              0・1・2歳児のための、<br />温かい「もうひとつの我が家」。
            </p>
            <p className="text-sm text-gray-500">多賀城市的の小規模認可保育園</p>
            <div className="flex flex-wrap gap-4">
              <a href="https://kirari-hoiku.com/tagajo/" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all hover:scale-105 font-medium flex items-center gap-2 cursor-pointer whitespace-nowrap shadow-lg">
                園見学のお申し込み
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-sm text-gray-700 flex items-center gap-2 shadow-sm">
                <i className="ri-restaurant-line text-orange-500"></i>
                無添加調味料の園内給食
              </div>
              <div className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-sm text-gray-700 flex items-center gap-2 shadow-sm">
                <i className="ri-parking-box-line text-orange-500"></i>
                駐車場完備
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-20 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <i className="ri-sun-line text-4xl text-orange-400"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">私たちの保育理念</h2>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
              子どもが人生の主役。0-2歳の大切な時期を、<br />第二の家族として支えます。
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl p-12 flex flex-col items-center justify-center shadow-xl text-white">
              <div className="text-center space-y-4">
                <i className="ri-line-chart-line text-7xl mb-2"></i>
                <p className="text-2xl font-bold">発達に特化した</p>
                <p className="text-3xl font-bold">きらりの発達ナビ導入</p>
                <p className="text-sm mt-4 leading-relaxed opacity-90">一人ひとりの発達段階を目を向けて、<br />最適な保育を提供します</p>
              </div>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500 text-white flex-shrink-0">
                  <i className="ri-heart-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">子ども中心の保育</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">一人ひとりの個性を大切にし、その子らしさを伸ばす保育を実践しています。</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500 text-white flex-shrink-0">
                  <i className="ri-parent-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">第二の家族として</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">家庭的な雰囲気の中で、安心して過ごせる「もうひとつの我が家」を目指します。</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-500 text-white flex-shrink-0">
                  <i className="ri-seedling-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">成長の土台づくり</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">0-2歳は人生の基礎を築く大切な時期。愛情深く見守り、育みます。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <h2 className="text-4xl font-bold text-gray-900">きらりの3つの特徴</h2>
            <p className="text-sm text-gray-500">Small-scale, Big Heart</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all hover:scale-105">
              <div className="h-64 w-full overflow-hidden">
                <img src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/ec6319544fa6184f2ed9fa7373b0c192.jpeg" alt="発達ナビ" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-6 space-y-3 text-white">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium">最新技術</span>
                <h3 className="text-xl font-bold">きらりの発達ナビ</h3>
                <p className="text-sm leading-relaxed opacity-90">お子さまの成長を保護者と一緒に共有します。</p>
              </div>
            </div>
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 w-full overflow-hidden">
                <img src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/49539994332b288ab8c03b8d368c9e68.jpeg" alt="健康的な食事" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-6 space-y-3">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-600 rounded-full text-xs font-medium">自園調理</span>
                <h3 className="text-xl font-bold text-gray-900">安心・安全な手作り給食</h3>
                <p className="text-sm text-gray-600 leading-relaxed">完全に無添加の調味料を導入した園内調理、栄養士監修の献立。</p>
              </div>
            </div>
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 w-full overflow-hidden">
                <img src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/863eb0d46979dc716e148c0d3a0f6809.jpeg" alt="保護者サポート" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-6 space-y-3">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">利便性</span>
                <h3 className="text-xl font-bold text-gray-900">働く保護者を全力サポート</h3>
                <p className="text-sm text-gray-600 leading-relaxed">JR岩切駅近くの利便性が好評です。大型駐車場完備。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="py-20 bg-[#FFF9E1]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">きらりでの一日</h2>
            <p className="text-lg text-gray-600">お子さまの成長に合わせた、ゆったりとした時間割</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-orange-300 -translate-x-1/2 hidden md:block"></div>
            <div className="space-y-8 md:space-y-8">
              {[
                { time: '7:30', title: '登園・自由遊び', desc: '順次登園。健康チェックと保護者との情報共有', icon: 'ri-door-open-line', side: 'left' },
                { time: '9:30', title: '朝の会・活動', desc: '朝の歌、手遊び、季節の活動や外遊び', icon: 'ri-music-line', side: 'right' },
                { time: '11:30', title: '昼食', desc: '自園調理の温かい給食をみんなで楽しく', icon: 'ri-restaurant-line', side: 'left' },
                { time: '12:30', title: 'お昼寝', desc: '一人ひとりのリズムに合わせた休息時間', icon: 'ri-zzz-line', side: 'right' },
                { time: '15:00', title: 'おやつ', desc: '手作りおやつで午後のエネルギー補給', icon: 'ri-cake-line', side: 'left' },
                { time: '16:00', title: '自由遊び・降園', desc: '室内遊びや絵本の読み聞かせ、順次降園', icon: 'ri-book-open-line', side: 'right' },
                { time: '19:30', title: '延長保育終了', desc: '最終降園まで安心してお預かり', icon: 'ri-home-heart-line', side: 'left' }
              ].map((item, index) => (
                <div key={index} className={`flex items-center gap-4 md:gap-8 ${item.side === 'right' ? 'md:flex-row-reverse' : ''}`}>
                  {/* Mobile: 左に時間 */}
                  <div className="w-16 h-16 md:hidden flex items-center justify-center rounded-full bg-orange-500 text-white font-bold text-sm shadow-lg flex-shrink-0">
                    {item.time}
                  </div>
                  {/* Mobile: 右に活動内容 */}
                  <div className="flex-1 md:hidden">
                    <div className="bg-white rounded-2xl p-4 shadow-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${item.icon} text-xl text-orange-500`}></i>
                        <h3 className="text-base font-bold text-gray-900">{item.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                  {/* Desktop: 従来のレイアウト */}
                  <div className={`flex-1 hidden md:block ${item.side === 'right' ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg inline-block w-full md:max-w-md">
                      <div className="flex items-center gap-3 mb-2">
                        <i className={`${item.icon} text-2xl text-orange-500`}></i>
                        <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                      </div>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                  <div className="w-20 h-20 hidden md:flex items-center justify-center rounded-full bg-orange-500 text-white font-bold text-lg shadow-lg flex-shrink-0 z-10">
                    {item.time}
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">園の様子</h2>
            <p className="text-lg text-gray-600">子どもたちの笑顔あふれる日常</p>
          </div>
          <div className="relative">
            {/* Carousel Container */}
            <div className="overflow-hidden rounded-3xl">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {galleryImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div className="w-full h-[400px] md:h-[600px]">
                      <img 
                        src={image.src} 
                        alt={image.alt} 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all hover:scale-110 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-800"></i>
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all hover:scale-110 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-800"></i>
            </button>
            
            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                    currentSlide === index 
                      ? 'bg-orange-500 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section id="access" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">アクセス・施設情報</h2>
              <div className="space-y-4">
                <div className="bg-[#FFF9E1] rounded-2xl p-5 flex items-start gap-4">
                  <i className="ri-map-pin-line text-2xl text-orange-500 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">住所</p>
                    <p className="text-base font-medium text-gray-900">〒985-0854<br />宮城県多賀城市新田後８−２</p>
                  </div>
                </div>
                <div className="bg-[#FFF9E1] rounded-2xl p-5 flex items-start gap-4">
                  <i className="ri-time-line text-2xl text-orange-500 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">開園時間</p>
                    <p className="text-base font-medium text-gray-900">7:30 - 19:30<br />(延長保育対応)</p>
                  </div>
                </div>
                <div className="bg-[#FFF9E1] rounded-2xl p-5 flex items-start gap-4">
                  <i className="ri-phone-line text-2xl text-orange-500 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">お問い合わせ</p>
                    <a href="tel:0222536601" className="text-base font-medium text-gray-900 hover:text-orange-500 transition-colors cursor-pointer">022-253-6601</a>
                    <p className="text-xs text-gray-500 mt-1">受付専用・折り返しお掛けします</p>
                  </div>
                </div>
                <div className="bg-[#FFF9E1] rounded-2xl p-5 flex items-start gap-4">
                  <i className="ri-parking-box-line text-2xl text-orange-500 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">駐車場</p>
                    <p className="text-base font-medium text-gray-900">大型駐車場完備(無料)</p>
                  </div>
                </div>
              </div>
              <a href="https://kirari-hoiku.com/tagajo/" target="_blank" rel="noopener noreferrer" className="w-full px-8 py-4 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all hover:scale-105 font-medium mt-8 cursor-pointer whitespace-nowrap block text-center">
                園見学を予約する
              </a>
            </div>
            <div className="space-y-4">
              <div className="rounded-3xl overflow-hidden shadow-lg relative h-[500px] w-full">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3130.5!2d140.9876!3d38.2987!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5f8a2f3b4c5d6e7f%3A0x1234567890abcdef!2z5a6u5Z-O55yM5aSa6LOA5Z-O5biC5paw55Sw5b6M77yY4oiS77yS!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="きらり保育多賀城の地図"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5EFE6] rounded-t-[48px] pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/8964f2db9296a8798ab5bb6895e36bf1.jpeg" alt="きらり保育多賀城" className="h-12 w-auto" />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                0・1・2歳児のための<br />
                温かいもうひとつの我が家
              </p>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-4">お問い合わせ</h3>
              <a href="tel:0222536601" className="text-2xl font-bold text-gray-900 hover:text-orange-500 transition-colors cursor-pointer">022-253-6601</a>
              <p className="text-xs text-gray-500 mt-1">受付専用・折り返しお掛けします</p>
              <p className="text-xs text-gray-500">受付時間: 平日9:00-17:00</p>
            </div>
          </div>
          <div className="border-t border-gray-300 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-600">
              <p>© 2025 きらり保育多賀城</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
